'use client';
import React, { createContext, useContext, useState, ReactNode, useRef  } from "react";
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimesCircle, FaShieldAlt, FaTimes } from "react-icons/fa";

type NotificationType = "info" | "success" | "warning" | "error"| "safety";

interface Notification {
  message: string;
  type?: NotificationType;
 actionLabel?: string;
  onAction?: () => void;
}

interface NotificationContextProps {
  showNotification: (
    message: string, 
    type?: NotificationType,
     actionLabel?: string,
    onAction?: () => void
) => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used within NotificationProvider");
  return ctx;
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [queue, setQueue] = useState<Notification[]>([]);
  const [current, setCurrent] = useState<Notification | null>(null);

  const showNotification = (
    message: string,
    type: NotificationType = "info",
    actionLabel?: string,
    onAction?: () => void
  ) => {
    setQueue(q => [...q, { message, type, actionLabel, onAction }]);
  };
  // Show next notification in queue
  React.useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue(q => q.slice(1));
    }
  }, [queue, current]);
 
  const handleClose = () => {
    setCurrent(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case "success":
        return <FaCheckCircle className="text-green-400 mr-2" />;
      case "error":
        return <FaTimesCircle className="text-red-400 mr-2" />;
      case "warning":
        return <FaExclamationTriangle className="text-yellow-400 mr-2" />;
      case "safety":
        return <FaShieldAlt className="text-purple-400 mr-2" />;
      default:
        return <FaInfoCircle className="text-blue-400 mr-2" />;
    }
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {current && (
        <div
          className={`
            fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center
            text-white font-semibold text-base
            transition-all
            ${current.type === "success" ? "bg-green-700" : ""}
            ${current.type === "error" ? "bg-red-700" : ""}
            ${current.type === "warning" ? "bg-yellow-700 text-black" : ""}
            ${current.type === "safety" ? "bg-purple-900 border border-purple-400" : ""}
            ${current.type === "info" ? "bg-purple-700" : ""}
          `}
         >
          {getIcon(current.type || "info")}
          <span className="flex-1">{current.message}</span>
          {current.actionLabel && current.onAction && (
            <button
              onClick={() => {
                current.onAction && current.onAction();
                handleClose();
              }}
              className="ml-4 px-3 py-1 rounded bg-white text-purple-700 font-bold hover:bg-purple-100 transition"
            >
              {current.actionLabel}
            </button>
          )}
          {/* Close button */}
             <button
            onClick={handleClose}
            className="ml-4 p-1 rounded hover:bg-purple-800 transition"
            aria-label="Close notification"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
      )}
    </NotificationContext.Provider>
  );
};